import { PlayerHeadshot, TeamLogo } from "@/components";
// eslint-disable-next-line no-restricted-imports -- DO NOT COPY THIS LINE. See docs/lints.md.
import { PlayerRow } from "@suns/api/generated-client/apollo";
import { Flex, Grid, Text } from "@suns/design-system";
import {
  ApolloAnalyticsIcon,
  ApolloScoutIcon,
} from "@suns/design-system/icons";
import PlayerRanks from "./PlayerRanks";
import { LeagueId } from "@suns/api";
import { CircleDotIcon, ArrowUpIcon, ArrowDownIcon } from "lucide-react";
import { theme } from "@/shared/theme";
import {
  ApolloGradeScopeLabels,
  ApolloGradeValueLabels,
} from "@/pages/reports/reports-const";
import {
  PlayerMetadataPositionLabels,
  PlayerMetadataRoleLabels,
} from "../players-const";

export default function PlayerProfileHeader({
  player,
  print = false,
}: {
  player: PlayerRow;
  print?: boolean;
}) {
  const playerMetadata = {
    Position: [
      PlayerMetadataPositionLabels[
        player.metadata[0]
          ?.scoutPosition as keyof typeof PlayerMetadataPositionLabels
      ],
    ],
    Role: [
      PlayerMetadataRoleLabels[
        player.metadata[0]?.scoutRole as keyof typeof PlayerMetadataRoleLabels
      ],
    ],
    Grade: [
      ApolloGradeValueLabels[
        player.metadata[0]?.scoutGradeCurrent
          ?.value as keyof typeof ApolloGradeValueLabels
      ],
      ApolloGradeValueLabels[
        player.metadata[0]?.analyticGradeCurrent
          ?.value as keyof typeof ApolloGradeValueLabels
      ],
    ],
    "Remaining Capacity": [
      ApolloGradeValueLabels[
        player.metadata[0]?.scoutGradeRemainingCapacity
          ?.value as keyof typeof ApolloGradeValueLabels
      ],
      ApolloGradeValueLabels[
        player.metadata[0]?.analyticGradeRemainingCapacity
          ?.value as keyof typeof ApolloGradeValueLabels
      ],
    ],
    Bullseye: [
      ApolloGradeValueLabels[
        player.metadata[0]?.scoutGradeBullseye
          ?.value as keyof typeof ApolloGradeValueLabels
      ],
      ApolloGradeValueLabels[
        player.metadata[0]?.analyticGradeBullseye
          ?.value as keyof typeof ApolloGradeValueLabels
      ],
    ],
    Ceiling: [
      ApolloGradeValueLabels[
        player.metadata[0]?.scoutGradeCeiling
          ?.value as keyof typeof ApolloGradeValueLabels
      ],
      ApolloGradeValueLabels[
        player.metadata[0]?.analyticGradeCeiling
          ?.value as keyof typeof ApolloGradeValueLabels
      ],
    ],
    Basement: [
      ApolloGradeValueLabels[
        player.metadata[0]?.scoutGradeBasement
          ?.value as keyof typeof ApolloGradeValueLabels
      ],
      ApolloGradeValueLabels[
        player.metadata[0]?.analyticGradeBasement
          ?.value as keyof typeof ApolloGradeValueLabels
      ],
    ],
  };

  return (
    <Flex gap="lg" align="end" className="overflow-hidden pt-2">
      {/* Desktop only */}
      <Flex className="relative max-sm:hidden" align="end">
        <div className="z-2 absolute -bottom-16 -left-20 size-56 opacity-15">
          <TeamLogo
            nbaTeamId={player.currentTeams?.[0]?.nbaId}
            leagueId={player.currentTeams?.[0]?.domesticLeagueId}
            src={player.currentTeams?.[0]?.image}
            className="h-full w-full"
          />
        </div>
        <PlayerHeadshot
          src={player.image}
          nbaPlayerId={player.nbaId}
          leagueId={player.currentTeams?.[0]?.domesticLeagueId}
          size="2xl"
          className="ml-2"
        />
      </Flex>
      {/* Player Info */}
      <Flex gap="sm" direction="down" className="flex-1">
        <Flex align="end" gap="lg" className="relative overflow-hidden">
          <Flex className="h-full sm:hidden">
            {player.currentTeams?.[0]?.domesticLeagueId !== LeagueId.NCAA ? (
              <div className="z-1 absolute -bottom-14 -left-6 size-48 opacity-15 sm:hidden">
                <TeamLogo
                  nbaTeamId={player.currentTeams?.[0]?.nbaId}
                  leagueId={player.currentTeams?.[0]?.domesticLeagueId}
                  src={player.currentTeams?.[0]?.image}
                  className="h-full w-full"
                />
              </div>
            ) : null}
            <PlayerHeadshot
              src={player.image}
              nbaPlayerId={player.nbaId}
              leagueId={player.currentTeams?.[0]?.domesticLeagueId}
              size="2xl"
            />
          </Flex>
          <Flex
            direction={["down", "md:right"]}
            justify="between"
            align="start"
            grow
          >
            <Flex direction="down" gap="xs">
              <Text size={print ? "xl" : "2xl"} heading>
                {player.firstName} {player.lastName}
              </Text>
              <Flex direction="right" gap="xs" align="center">
                <TeamLogo
                  src={player.currentTeams?.[0]?.image}
                  nbaTeamId={player.currentTeams?.[0]?.nbaId}
                  leagueId={player.currentTeams?.[0]?.domesticLeagueId}
                  size={print ? "3xs" : "2xs"}
                />
                <Text size="xs">
                  {player.currentTeams?.[0]?.fullName ?? "Free Agent"}
                </Text>
              </Flex>
            </Flex>
            <PlayerRanks player={player} size={print ? "sm" : "md"} />
          </Flex>
        </Flex>

        <Flex
          gap={print ? "md" : "lg"}
          justify={print ? "between" : undefined}
          className="mb-2 max-sm:hidden"
        >
          {Object.entries(playerMetadata).map(([key, value]) => {
            const [scout, analytics] = value;
            if (!scout && !analytics) return null;
            return (
              <Flex key={key} direction="down" gap="xs">
                <Flex direction="right" gap="xs" align="center">
                  {key === ApolloGradeScopeLabels.BULLSEYE && (
                    <CircleDotIcon size={12} className="text-gray-500" />
                  )}
                  {key === ApolloGradeScopeLabels.CEILING && (
                    <ArrowUpIcon size="12" className="text-gray-500" />
                  )}
                  {key === ApolloGradeScopeLabels.BASEMENT && (
                    <ArrowDownIcon size="12" className="text-gray-500" />
                  )}
                  <Text size={print ? "3xs" : "xs"} heading muted>
                    {key}
                  </Text>
                </Flex>

                {key === "Position" || key === "Role" ? (
                  <Text size={print ? "xs" : "md"} heading>
                    {scout ?? "--"}
                  </Text>
                ) : (
                  <>
                    <Flex direction="right" gap="xs" align="center">
                      <ApolloScoutIcon
                        size={print ? 16 : 22}
                        color={theme.colors.teal.DEFAULT}
                      />
                      <Text size={print ? "xs" : "md"} heading>
                        {scout ?? "--"}
                      </Text>
                    </Flex>
                    <Flex direction="right" gap="xs" align="center">
                      <ApolloAnalyticsIcon
                        size={print ? 16 : 22}
                        color={theme.colors.magenta.DEFAULT}
                      />
                      <Text size={print ? "xs" : "md"} heading>
                        {analytics ?? "--"}
                      </Text>
                    </Flex>
                  </>
                )}
              </Flex>
            );
          })}
        </Flex>
        {/* Mobile only */}
        <Flex direction="right" gap="md" className="my-2 w-full pl-5 sm:hidden">
          <Flex direction="down" className="w-full">
            <Grid columns="2" gap="md">
              {Object.entries(playerMetadata).map(([key, value]) => {
                const [scout, analytics] = value;
                if (!scout && !analytics) return null;
                return (
                  <Flex key={key} direction="down" gap="xs">
                    <Flex direction="right" gap="xs" align="center">
                      {key === ApolloGradeScopeLabels.BULLSEYE && (
                        <CircleDotIcon size={12} className="text-gray-500" />
                      )}
                      {key === ApolloGradeScopeLabels.CEILING && (
                        <ArrowUpIcon size="12" className="text-gray-500" />
                      )}
                      {key === ApolloGradeScopeLabels.BASEMENT && (
                        <ArrowDownIcon size="12" className="text-gray-500" />
                      )}
                      <Text size={print ? "3xs" : "xs"} heading muted>
                        {key}
                      </Text>
                    </Flex>

                    {key === "Position" || key === "Role" ? (
                      <Text size={print ? "xs" : "md"} heading>
                        {scout ?? "--"}
                      </Text>
                    ) : (
                      <>
                        <Flex direction="right" gap="xs" align="center">
                          <ApolloScoutIcon
                            size={print ? 16 : 22}
                            color={theme.colors.teal.DEFAULT}
                          />
                          <Text size={print ? "xs" : "md"} heading>
                            {scout ?? "--"}
                          </Text>
                        </Flex>
                        <Flex direction="right" gap="xs" align="center">
                          <ApolloAnalyticsIcon
                            size={print ? 16 : 22}
                            color={theme.colors.magenta.DEFAULT}
                          />
                          <Text size={print ? "xs" : "md"} heading>
                            {analytics ?? "--"}
                          </Text>
                        </Flex>
                      </>
                    )}
                  </Flex>
                );
              })}
            </Grid>
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
}
